import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import ActionContainer from '../../../../components/ActionContainer';
import Button from '../../../../components/Button';
import * as events from '../../../../lib/analytics/events';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { expectUnreachable } from '../../../../lib/util';
import * as types from '../../../../types';
import styles from './Billing.module.scss';

function CreditCardPaymentInfo(props: { paymentInfo: types.CreditCardInfo }) {
  return (
    <>
      <div className={styles.creditCardBrand}>{props.paymentInfo.type}</div>
      {/* sentry-mask class is used to mask text in Sentry replays */}
      <div className={styles.creditCardNumber}>
        <Trans
          i18nKey="card_ending_in_last_four"
          values={{
            lastFour: props.paymentInfo.lastFour,
          }}
          components={{ mask: <span className="sentry-mask" /> }}
        />
      </div>
      <div className={styles.creditCardExp}>
        <Trans
          i18nKey="exp_month_year"
          values={{
            month: props.paymentInfo.month,
            year: props.paymentInfo.year,
          }}
          components={{ mask: <span className="sentry-mask" /> }}
        />
      </div>
    </>
  );
}

export function PayPalPaymentInfo() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    checkoutActions,
    checkoutState: { billingInfo },
  } = useContext(CheckoutContext);

  const handleUnlink = useCallback(() => {
    dispatch(checkoutActions.unlinkBillingInfo({}));
    events.payment.paypalUnlink();
  }, [checkoutActions, dispatch]);

  if (!billingInfo) {
    return null;
  }

  return (
    <>
      <div>
        {t('paid_with')}{' '}
        <img
          src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
          alt={t('pay_with_paypal')}
          style={{ height: '1em', verticalAlign: 'text-bottom' }}
        />
      </div>
      <div>
        <span className={styles.unlinkBillingCta} onClick={handleUnlink}>
          {t('unlink_paypal')}
        </span>
      </div>
    </>
  );
}

interface ViewBillingInfoProps {
  billingInfo: types.BillingInfo;
  onEdit(): void;
  onContinue(): void;
}

export default function ViewBillingInfo({ billingInfo, onContinue, onEdit }: ViewBillingInfoProps) {
  const { t } = useTranslation();
  const paymentInfo = useMemo(() => {
    if (billingInfo.paymentInfo.__typename === 'ObfuscatedCardInfo') {
      return <CreditCardPaymentInfo paymentInfo={billingInfo.paymentInfo} />;
    } else if (billingInfo.paymentInfo.__typename === 'PayPalInfo') {
      return <PayPalPaymentInfo />;
    } else if (billingInfo.paymentInfo.__typename === 'BankAccountInfo') {
      const { bankName, last4 } = billingInfo.paymentInfo;
      return (
        <div>
          <span className="sentry-mask">
            {bankName ? `${bankName} ` : ''}
            {last4 ? t('bank_account_ending_in', { last4 }) : t('bank_account')}
          </span>
        </div>
      );
    } else {
      expectUnreachable(billingInfo.paymentInfo);
    }
  }, [billingInfo, t]);

  const handleEdit = useCallback(
    (event) => {
      event.preventDefault();
      onEdit();
    },
    [onEdit],
  );

  const handleContinue = useCallback(
    (event) => {
      event.preventDefault();
      onContinue();
    },
    [onContinue],
  );

  return (
    <div className={styles.viewBillingInfo}>
      <div className={styles.billingDetails}>
        {/* sentry-mask class is used to mask text in Sentry replays */}
        <div className="sentry-mask">
          {billingInfo.firstName} {billingInfo.lastName}
        </div>
        {paymentInfo}
      </div>
      <ActionContainer>
        <Button secondary onClick={handleEdit}>
          {t('edit')}
        </Button>
        <Button onClick={handleContinue}>{t('next')}</Button>
      </ActionContainer>
    </div>
  );
}
